<template>
    <div id="product-price-table" :data-shortcut-title="$t('general.prices.title')" class="w-full md:h-full">
        <MaxHeightCollapse :enabled="isInSliceGroup" class="rounded-lg bg-white md:h-full md:border md:border-border md:p-6">
            <MobileCollapsibleSection>
                <!-- Smaller as an accordion row in the product details group, like the FAQs and specifications. -->
                <template #title="{ isCollapsible }">
                    <div class="flex items-center justify-between gap-4">
                        <span
                            class="font-extrabold md:text-2xl md:font-black onlywow:font-black onlywow:md:text-4xl"
                            :class="isCollapsible ? 'text-xl' : 'text-2xl onlywow:text-xl'"
                            v-text="$t('general.prices.title')"
                        />

                        <!-- When discount blocks are present, show the "excl. discount" label instead of the discount badge -->
                        <span v-if="productPageStore.hasDiscountBlocks" class="font-bold" v-text="$t('general.prices.excl_discount.label')" />
                        <DiscountBadge v-else :discount="productPageStore.configurableProduct.discount" size="xl" />
                    </div>
                </template>

                <template #default="{ isCollapsible }">
                    <div class="space-y-5" :class="{ 'pt-2 md:pt-0': isCollapsible }">
                        <ProductPriceTableProductGroup
                            v-for="(productGroup, index) in productGroups"
                            :key="`price_table_group_${index}`"
                            :group-title="index as string"
                            :product-groups="productGroup"
                            :show-group-title="Object.keys(productGroups).length > 1"
                            :show-all-on-desktop="isInSliceGroup"
                        />
                    </div>
                </template>
            </MobileCollapsibleSection>

            <template #toggle="{ isExpanded, toggle }">
                <button type="button" class="cursor-pointer text-quaternary underline outline-none" :aria-expanded="isExpanded" @click="toggle">
                    {{ isExpanded ? $t('general.price_table.filters.show_less') : $t('general.price_table.filters.show_more') }}
                </button>
            </template>
        </MaxHeightCollapse>
    </div>
</template>

<script lang="ts" setup>
import MobileCollapsibleSection from '@/components/accordions/MobileCollapsibleSection.vue';
import DiscountBadge from '@/components/badges/DiscountBadge.vue';
import MaxHeightCollapse from '@/components/collapsible/MaxHeightCollapse.vue';
import { isInSliceGroupKey } from '@/composables/prismic/sliceGroupContext';
import { filterPriceTableProducts } from '@/lib/products/priceTable';
import ProductPriceTableProductGroup from '@/pages/product-page/partials/product-price-table/ProductPriceTableProductGroup.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { useProductStore } from '@/stores/products/ProductStore';
import type { ConfigurableProductProductObject } from '@/types/generated';
import _ from 'lodash';
import { computed, inject } from 'vue';

const productPageStore = useProductPageStore();
const productStore = useProductStore();

const isInSliceGroup = inject(isInSliceGroupKey, false);

// Narrowed by the product page filters the magento group picked for the price table.
const allProducts = computed(() =>
    filterPriceTableProducts(
        productPageStore.configurableProduct.configurable_product_products,
        productPageStore.configurableProduct.price_table_filters,
        productStore.activeAttributeValues,
    ),
);

const hasFeaturedProducts = computed(() => allProducts.value.some((cpp: ConfigurableProductProductObject) => cpp.metadata.is_featured));

// Mirror Magento 2: when featured products exist, only show those in the price table
const productGroups = computed(() =>
    _(allProducts.value)
        .filter((cpp: ConfigurableProductProductObject) => !_.isNil(cpp.metadata.price_table_entry))
        .filter((cpp: ConfigurableProductProductObject) => !hasFeaturedProducts.value || cpp.metadata.is_featured)
        .groupBy((cpp: ConfigurableProductProductObject) => cpp.metadata.price_table_entry?.group_title)
        .value(),
);
</script>
